import { type CSSProperties, useEffect, useMemo, useState } from "react";

type ProfitabilitySummary = {
  count: number;
  wins: number;
  losses: number;
  win_rate: number | null;
  pnl: number;
  avg_pnl: number;
};

type LeakBucket = {
  dimension: string;
  bucket: string;
  count: number;
  wins: number;
  losses: number;
  win_rate: number | null;
  pnl: number;
  avg_pnl: number;
};

type CandidateFilter = LeakBucket & {
  hypothetical_exclude_pnl_delta: number;
  hypothetical_retained_count: number;
  hypothetical_retained_pnl: number;
  hypothetical_retained_win_rate: number | null;
  promotion_status: "watch_only" | "needs_more_sample" | "needs_forward_validation";
  live_gate_change: false;
};

export type AgentProfitability = {
  summary: ProfitabilitySummary;
  sample_status?: {
    status: "no_sample" | "insufficient_sample" | "decision_grade";
    count: number;
    min_decision_sample: number;
    decision_grade: boolean;
    latest_closed_date?: string | null;
    days_since_latest_closed?: number | null;
    stale_after_days?: number;
    recency_status?: "no_recent_closed_trades" | "stale" | "recent";
  };
  opportunity_status?: {
    status: "no_observations" | "stale_observations" | "observing";
    observation_count: number;
    latest_observation_date?: string | null;
    days_since_latest_observation?: number | null;
    status_counts?: Array<{ status: string; count: number }>;
    top_reasons?: Array<{ reason: string; count: number }>;
    symbols_observed?: Array<{ symbol: string; count: number }>;
  };
  top_leaks: LeakBucket[];
  candidate_filters: CandidateFilter[];
};

type PromotionReadinessStatus = "manual_review" | "monitor" | "collecting" | "blocked";

type PromotionReadiness = {
  status: PromotionReadinessStatus;
  label: string;
  reason: string;
};

export type PromotionEvidenceRow = {
  agent: string;
  readiness: PromotionReadiness;
  sample: string;
  recency: string;
  opportunity: string;
  winRate: string;
  pnl: string;
  candidates: number;
  ctoAction: string;
};

type ProfitabilityInsights = {
  schema_version: string;
  generated_at: string;
  source: {
    since: string;
    filtered_clean_rows: number;
    read_only: boolean;
    note: string;
  };
  orb: AgentProfitability;
  agents?: Record<string, AgentProfitability>;
};

type LoadState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "success"; data: ProfitabilityInsights };

const API_BASE = "https://qstrades-api.aizo.workers.dev/api";

const S = {
  wrap: {
    background: "rgba(12,12,24,0.85)",
    border: "1px solid rgba(255,255,255,0.06)",
    borderRadius: "8px",
    padding: "10px",
    marginBottom: "12px",
  },
  agentTitle: {
    marginTop: "10px",
    marginBottom: "6px",
    fontSize: "9px",
    fontWeight: 900,
    letterSpacing: "0.12em",
    color: "#e0e0f0",
    textTransform: "uppercase" as const,
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    paddingBottom: "6px",
    marginBottom: "6px",
    borderBottom: "1px solid rgba(255,255,255,0.08)",
  },
  title: {
    fontSize: "10px",
    fontWeight: 800,
    letterSpacing: "0.12em",
    color: "#a0a0c0",
    textTransform: "uppercase" as const,
  },
  meta: {
    marginLeft: "auto",
    fontSize: "9px",
    color: "#707090",
  },
  summary: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: "8px",
    marginBottom: "8px",
  },
  stat: {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.05)",
    borderRadius: "6px",
    padding: "6px",
  },
  statLabel: {
    display: "block",
    fontSize: "8px",
    color: "#707090",
    textTransform: "uppercase" as const,
    letterSpacing: "0.08em",
  },
  statValue: {
    display: "block",
    marginTop: "2px",
    fontSize: "12px",
    fontWeight: 800,
    color: "#e0e0f0",
  },
  row: {
    display: "grid",
    gridTemplateColumns: "1.2fr 0.8fr 48px 58px 64px",
    alignItems: "center",
    gap: "6px",
    padding: "4px 0",
    borderBottom: "1px solid rgba(255,255,255,0.03)",
  },
  candidateRow: {
    display: "grid",
    gridTemplateColumns: "1.2fr 0.8fr 62px 72px 96px",
    alignItems: "center",
    gap: "6px",
    padding: "4px 0",
    borderBottom: "1px solid rgba(255,255,255,0.03)",
  },
  headCell: {
    fontSize: "8px",
    fontWeight: 700,
    letterSpacing: "0.1em",
    color: "#707090",
    textTransform: "uppercase" as const,
  },
  cell: {
    fontSize: "9px",
    color: "#c0c0d0",
    minWidth: 0,
  },
  risk: {
    color: "#ef4444",
    fontWeight: 800,
  },
  sampleBadge: {
    display: "inline-block",
    marginLeft: "8px",
    padding: "2px 6px",
    borderRadius: "999px",
    fontSize: "8px",
    fontWeight: 900,
    letterSpacing: "0.08em",
    textTransform: "uppercase" as const,
  },
  sectionLabel: {
    marginTop: "8px",
    marginBottom: "4px",
    fontSize: "8px",
    fontWeight: 800,
    letterSpacing: "0.1em",
    color: "#94a3b8",
    textTransform: "uppercase" as const,
  },
  note: {
    marginTop: "6px",
    fontSize: "9px",
    color: "#707090",
  },
  opportunity: {
    margin: "-2px 0 8px",
    fontSize: "9px",
    color: "#94a3b8",
  },
  readinessGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: "8px",
    marginBottom: "10px",
  },
  readinessCard: {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.06)",
    borderRadius: "7px",
    padding: "7px",
  },
  readinessAgent: {
    display: "block",
    marginBottom: "4px",
    fontSize: "8px",
    fontWeight: 900,
    letterSpacing: "0.12em",
    color: "#94a3b8",
    textTransform: "uppercase" as const,
  },
  readinessLabel: {
    display: "block",
    fontSize: "11px",
    fontWeight: 900,
    textTransform: "uppercase" as const,
  },
  readinessReason: {
    display: "block",
    marginTop: "4px",
    fontSize: "8px",
    color: "#94a3b8",
    lineHeight: 1.35,
  },
  evidenceRow: {
    display: "grid",
    gridTemplateColumns: "0.55fr 0.9fr 0.9fr 0.85fr 1fr 0.55fr 0.75fr 0.75fr 1.35fr",
    alignItems: "center",
    gap: "6px",
    padding: "4px 0",
    borderBottom: "1px solid rgba(255,255,255,0.03)",
  },
};

function money(value: number): string {
  const sign = value >= 0 ? "+" : "-";
  return `${sign}$${Math.round(Math.abs(value)).toLocaleString()}`;
}

function pct(value: number | null): string {
  if (value === null || value === undefined) return "--";
  return `${Math.round(value * 100)}%`;
}

function ageLabel(generatedAt: string): string {
  const ms = Date.parse(generatedAt);
  if (!Number.isFinite(ms)) return "generated time unknown";
  const delta = Math.max(0, Date.now() - ms);
  if (delta < 60_000) return "generated just now";
  if (delta < 3_600_000) return `generated ${Math.round(delta / 60_000)}m ago`;
  return `generated ${Math.round(delta / 3_600_000)}h ago`;
}

function dimensionLabel(value: string): string {
  return value.replaceAll("_", " ");
}

function promotionLabel(value: CandidateFilter["promotion_status"]): string {
  if (value === "needs_forward_validation") return "Forward validate";
  if (value === "needs_more_sample") return "More sample";
  return "Watch only";
}

function sampleLabel(status: AgentProfitability["sample_status"]): string {
  if (!status) return "Sample unknown";
  const recency = status.recency_status === "stale" && status.days_since_latest_closed !== null && status.days_since_latest_closed !== undefined
    ? ` · stale ${status.days_since_latest_closed}d`
    : status.recency_status === "no_recent_closed_trades"
      ? " · no recent closes"
      : "";
  if (status.status === "decision_grade") return `Decision-grade sample (${status.count}/${status.min_decision_sample})${recency}`;
  if (status.status === "no_sample") return `No sample (${status.count}/${status.min_decision_sample})${recency}`;
  return `Insufficient sample (${status.count}/${status.min_decision_sample})${recency}`;
}

function sampleBadgeStyle(status: AgentProfitability["sample_status"]): CSSProperties {
  if (status?.status === "decision_grade" && status.recency_status === "recent") {
    return { ...S.sampleBadge, color: "#22c55e", background: "rgba(34,197,94,0.12)", border: "1px solid rgba(34,197,94,0.28)" };
  }
  return { ...S.sampleBadge, color: "#f59e0b", background: "rgba(245,158,11,0.12)", border: "1px solid rgba(245,158,11,0.28)" };
}

function opportunityLabel(status: AgentProfitability["opportunity_status"]): string {
  if (!status) return "Opportunity capture unknown";
  const latest = status.latest_observation_date ? `latest ${status.latest_observation_date}` : "no latest observation";
  const topReason = status.top_reasons?.[0] ? ` · top reason ${status.top_reasons[0].reason} (${status.top_reasons[0].count})` : "";
  if (status.status === "observing") return `Opportunity capture active — ${status.observation_count} observations, ${latest}${topReason}`;
  if (status.status === "stale_observations") return `Opportunity capture stale — ${status.observation_count} observations, ${latest}${topReason}`;
  return "Opportunity capture missing — no setup observations";
}

export function buildPromotionReadiness(payload: AgentProfitability): PromotionReadiness {
  const sample = payload.sample_status;
  const opportunity = payload.opportunity_status;
  const candidateCount = payload.candidate_filters.filter((row) => row.promotion_status === "needs_forward_validation").length;

  if (!sample || sample.status === "no_sample") {
    return { status: "blocked", label: "Blocked", reason: "No closed-trade sample yet" };
  }
  if (sample.status === "insufficient_sample") {
    return { status: "collecting", label: "Collecting", reason: `Need ${sample.min_decision_sample - sample.count} more closed trades for decision-grade sample` };
  }
  if (sample.recency_status === "stale" || sample.recency_status === "no_recent_closed_trades") {
    return { status: "blocked", label: "Blocked", reason: "Closed-trade sample is stale" };
  }
  if (!opportunity || opportunity.status === "no_observations") {
    return { status: "monitor", label: "Monitor", reason: "Decision-grade sample, but no setup-observation stream" };
  }
  if (opportunity.status === "stale_observations") {
    return { status: "blocked", label: "Blocked", reason: "Setup-observation stream is stale" };
  }
  if (candidateCount > 0) {
    return { status: "manual_review", label: "Review", reason: `${candidateCount} LOG-ONLY filter candidate${candidateCount === 1 ? "" : "s"} ready for forward-validation review` };
  }
  return { status: "monitor", label: "Monitor", reason: "Decision-grade sample; no profitable filter candidate yet" };
}

function sampleEvidence(status: AgentProfitability["sample_status"]): string {
  if (!status) return "unknown";
  return `${status.count}/${status.min_decision_sample} closed`;
}

function recencyEvidence(status: AgentProfitability["sample_status"]): string {
  if (!status) return "unknown";
  if (status.recency_status === "recent") return "recent";
  if (status.recency_status === "stale" && status.days_since_latest_closed !== null && status.days_since_latest_closed !== undefined) {
    return `stale ${status.days_since_latest_closed}d`;
  }
  if (status.recency_status === "no_recent_closed_trades") return "no recent closes";
  return "unknown";
}

function opportunityEvidence(status: AgentProfitability["opportunity_status"]): string {
  if (!status) return "unknown";
  if (status.status === "no_observations") return "0 observations";
  const age = status.days_since_latest_observation !== null && status.days_since_latest_observation !== undefined
    ? ` · ${status.days_since_latest_observation}d old`
    : "";
  return `${status.observation_count.toLocaleString()} observations${age}`;
}

function ctoActionForReadiness(readiness: PromotionReadiness): string {
  if (readiness.status === "manual_review") return "Review forward-validation candidate before any gate change";
  if (readiness.status === "collecting") return "Keep collecting; no strategy change";
  if (readiness.status === "blocked") return "Do not promote; fix evidence gap first";
  return "Monitor; no profitable promotion candidate yet";
}

export function buildPromotionEvidenceRows(agentEntries: Array<readonly [string, AgentProfitability]>): PromotionEvidenceRow[] {
  return agentEntries.map(([agent, payload]) => {
    const readiness = buildPromotionReadiness(payload);
    return {
      agent: AGENT_LABELS[agent] ?? agent.toUpperCase(),
      readiness,
      sample: sampleEvidence(payload.sample_status),
      recency: recencyEvidence(payload.sample_status),
      opportunity: opportunityEvidence(payload.opportunity_status),
      winRate: pct(payload.summary.win_rate),
      pnl: money(payload.summary.pnl),
      candidates: payload.candidate_filters.filter((row) => row.promotion_status === "needs_forward_validation").length,
      ctoAction: ctoActionForReadiness(readiness),
    };
  });
}

function readinessColor(status: PromotionReadinessStatus): string {
  if (status === "manual_review") return "#22c55e";
  if (status === "monitor") return "#38bdf8";
  if (status === "collecting") return "#f59e0b";
  return "#ef4444";
}

const AGENT_LABELS: Record<string, string> = { orb: "ORB", fbd: "FBD", mr: "MR" };

export function ProfitabilityInsightsPanel() {
  const [state, setState] = useState<LoadState>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem("qst_token");
    if (!token) {
      setState({ status: "error", message: "Admin session token unavailable" });
      return () => { cancelled = true; };
    }
    fetch(`${API_BASE}/admin/profitability-insights`, {
      cache: "no-store",
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<ProfitabilityInsights>;
      })
      .then((data) => {
        if (!cancelled) setState({ status: "success", data });
      })
      .catch((err: unknown) => {
        if (!cancelled) setState({ status: "error", message: err instanceof Error ? err.message : String(err) });
      });
    return () => { cancelled = true; };
  }, []);

  const content = useMemo(() => {
    if (state.status !== "success") return null;
    const agents = state.data.agents ?? { orb: state.data.orb };
    const agentEntries = ["orb", "fbd", "mr"].reduce<Array<readonly [string, AgentProfitability]>>((acc, agent) => {
      const payload = agents[agent];
      if (payload) acc.push([agent, payload] as const);
      return acc;
    }, []);
    const evidenceRows = buildPromotionEvidenceRows(agentEntries);
    return (
      <>
        <div style={S.readinessGrid} aria-label="Strategy promotion readiness">
          {agentEntries.map(([agent, payload]) => {
            const label = AGENT_LABELS[agent] ?? agent.toUpperCase();
            const readiness = buildPromotionReadiness(payload);
            return (
              <div key={`readiness:${agent}`} style={S.readinessCard}>
                <span style={S.readinessAgent}>{label}</span>
                <span style={{ ...S.readinessLabel, color: readinessColor(readiness.status) }}>{readiness.label}</span>
                <span style={S.readinessReason}>{readiness.reason}</span>
              </div>
            );
          })}
        </div>
        <div style={S.sectionLabel}>Promotion evidence scoreboard — CTO gate</div>
        <div role="table" aria-label="Strategy promotion evidence scoreboard">
          <div style={S.evidenceRow} role="row">
            <span style={S.headCell}>Agent</span><span style={S.headCell}>Readiness</span><span style={S.headCell}>Sample</span><span style={S.headCell}>Recency</span><span style={S.headCell}>Opportunity</span><span style={S.headCell}>WR</span><span style={S.headCell}>P&L</span><span style={S.headCell}>Candidates</span><span style={S.headCell}>CTO action</span>
          </div>
          {evidenceRows.map((row) => (
            <div style={S.evidenceRow} role="row" key={`evidence:${row.agent}`}>
              <span style={{ ...S.cell, fontWeight: 900, color: "#e0e0f0" }}>{row.agent}</span>
              <span style={{ ...S.cell, color: readinessColor(row.readiness.status), fontWeight: 900 }}>{row.readiness.label}</span>
              <span style={S.cell}>{row.sample}</span>
              <span style={S.cell}>{row.recency}</span>
              <span style={S.cell}>{row.opportunity}</span>
              <span style={S.cell}>{row.winRate}</span>
              <span style={{ ...S.cell, color: row.pnl.startsWith("+") ? "#22c55e" : "#ef4444", fontWeight: 800 }}>{row.pnl}</span>
              <span style={S.cell}>{row.candidates}</span>
              <span style={S.cell}>{row.ctoAction}</span>
            </div>
          ))}
        </div>
        {agentEntries.map(([agent, payload]) => {
          const { summary, top_leaks: topLeaks = [], candidate_filters: candidateFilters = [] } = payload;
          const label = AGENT_LABELS[agent] ?? agent.toUpperCase();
          return (
            <div key={agent}>
              <div style={S.agentTitle}>{label}<span style={sampleBadgeStyle(payload.sample_status)}>{sampleLabel(payload.sample_status)}</span></div>
              <div style={S.opportunity}>{opportunityLabel(payload.opportunity_status)}</div>
              <div style={S.summary} aria-label={`${label} profitability summary`}>
                <div style={S.stat}><span style={S.statLabel}>Clean rows</span><span style={S.statValue}>{summary.count}</span></div>
                <div style={S.stat}><span style={S.statLabel}>Win rate</span><span style={S.statValue}>{pct(summary.win_rate)}</span></div>
                <div style={S.stat}><span style={S.statLabel}>P&L</span><span style={{ ...S.statValue, color: summary.pnl >= 0 ? "#22c55e" : "#ef4444" }}>{money(summary.pnl)}</span></div>
                <div style={S.stat}><span style={S.statLabel}>Record</span><span style={S.statValue}>{summary.wins}W / {summary.losses}L</span></div>
              </div>
              <div role="table" aria-label={`Top ${label} leak buckets`}>
                <div style={S.row} role="row">
                  <span style={S.headCell}>Dimension</span><span style={S.headCell}>Bucket</span><span style={S.headCell}>N</span><span style={S.headCell}>WR</span><span style={S.headCell}>Leak</span>
                </div>
                {topLeaks.slice(0, 5).map((row) => (
                  <div style={S.row} role="row" key={`${agent}:${row.dimension}:${row.bucket}`}>
                    <span style={S.cell}>{dimensionLabel(row.dimension)}</span>
                    <span style={S.cell}>{row.bucket}</span>
                    <span style={S.cell}>{row.count}</span>
                    <span style={S.cell}>{pct(row.win_rate)}</span>
                    <span style={{ ...S.cell, ...S.risk }}>{money(row.pnl)}</span>
                  </div>
                ))}
                {topLeaks.length === 0 && <div style={S.note}>No losing buckets above the sample threshold.</div>}
              </div>
              {candidateFilters.length > 0 && (
                <>
                  <div style={S.sectionLabel}>Promotion candidates — LOG ONLY</div>
                  <div role="table" aria-label={`Hypothetical ${label} filter candidates`}>
                    <div style={S.candidateRow} role="row">
                      <span style={S.headCell}>Dimension</span><span style={S.headCell}>Bucket</span><span style={S.headCell}>Avoid</span><span style={S.headCell}>Kept WR</span><span style={S.headCell}>Status</span>
                    </div>
                    {candidateFilters.slice(0, 3).map((row) => (
                      <div style={S.candidateRow} role="row" key={`candidate:${agent}:${row.dimension}:${row.bucket}`}>
                        <span style={S.cell}>{dimensionLabel(row.dimension)}</span>
                        <span style={S.cell}>{row.bucket}</span>
                        <span style={{ ...S.cell, color: "#22c55e", fontWeight: 800 }}>{money(row.hypothetical_exclude_pnl_delta)}</span>
                        <span style={S.cell}>{pct(row.hypothetical_retained_win_rate)}</span>
                        <span style={S.cell}>{promotionLabel(row.promotion_status)}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </>
    );
  }, [state]);

  if (state.status === "loading") {
    return <section style={S.wrap}><div style={S.note}>Loading profitability insights…</div></section>;
  }
  if (state.status === "error") {
    return <section style={S.wrap}><div style={S.note}>Profitability insights unavailable: {state.message}</div></section>;
  }

  return (
    <section style={S.wrap} aria-label="Profitability insights">
      <div style={S.header}>
        <span style={S.title}>Profitability Leak Watch</span>
        <span style={S.meta}>ORB/FBD/MR · since {state.data.source.since} · {ageLabel(state.data.generated_at)}</span>
      </div>
      {content}
      <div style={S.note}>Read-only diagnostics. These buckets do not change live gates, thresholds, alerts, routing, or execution.</div>
    </section>
  );
}
